import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, createBurnInstruction, getAssociatedTokenAddress } from '@solana/spl-token';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import logger from '../utils/logger';

class TokenBurnService {
  constructor() {
    this.TOPBLAST_CA = process.env.REACT_APP_TOPBLAST_TOKEN_ADDRESS || 'F4XcNucxLXkNywr27994xhJLcZ8sKbWqoaS8xf47pump';
    this.BURN_ADDRESS = '1nc1nerator11111111111111111111111111111111'; // Common burn address
    // Use public RPC endpoints with better rate limits
    this.connection = new Connection('https://solana-mainnet.g.alchemy.com/v2/alch-demo', {
      commitment: 'confirmed',
      confirmTransactionInitialTimeout: 60000
    });
  }

  // Burn amounts for different boosts
  getBurnAmounts() {
    return {
      xp_2x_1hr: 10000,      // 10,000 tokens for 2x XP for 1 hour
      xp_3x_1hr: 250000,     // 250,000 tokens for 3x XP for 1 hour
      xp_5x_1hr: 1000000,    // 1,000,000 tokens for 5x XP for 1 hour
      extra_predictions: 500, // 500 tokens for +10 daily predictions
      refresh_prediction_limit: 10000, // 10,000 tokens to refresh daily prediction limit
      custom_badge: 2000,    // 2000 tokens for custom badge
      blue_check: 5000       // 5000 tokens for verified status
    };
  }

  // Calculate token amount with decimals
  async getTokenAmount(amount, tokenMint) {
    try {
      const mintPubkey = new PublicKey(tokenMint);
      const mintInfo = await this.connection.getParsedAccountInfo(mintPubkey);
      const decimals = mintInfo?.value?.data?.parsed?.info?.decimals || 9;
      return amount * Math.pow(10, decimals);
    } catch (error) {
      logger.error('Error getting token decimals:', error);
      return amount * Math.pow(10, 9); // Default to 9 decimals
    }
  }

  // Create and send burn transaction
  async createBurnTransaction(walletAddress, tokenAmount) {
    try {
      logger.log('🔥 Creating burn transaction...');

      // Get wallet provider (Phantom)
      const { solana } = window;
      if (!solana || !solana.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const tokenMint = this.TOPBLAST_CA;

      const walletPubkey = new PublicKey(walletAddress);
      const mintPubkey = new PublicKey(tokenMint);

      // Get user's token account
      const tokenAccount = await getAssociatedTokenAddress(
        mintPubkey,
        walletPubkey
      );

      logger.log(`Token account: ${tokenAccount.toString()}`);

      // Get token decimals
      const rawAmount = await this.getTokenAmount(tokenAmount, tokenMint);
      logger.log(`Burning ${tokenAmount} tokens (${rawAmount} raw amount)`);

      // Create burn instruction
      const burnIx = createBurnInstruction(
        tokenAccount,
        mintPubkey,
        walletPubkey,
        rawAmount,
        [],
        TOKEN_PROGRAM_ID
      );

      // Create transaction
      const transaction = new Transaction().add(burnIx);

      // Get recent blockhash
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = walletPubkey;

      logger.log('📝 Requesting signature from wallet...');

      // Sign and send transaction
      const signed = await solana.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signed.serialize());

      logger.log(`🔄 Transaction sent: ${signature}`);
      logger.log(`View on Solscan: https://solscan.io/tx/${signature}`);

      // Wait for confirmation
      logger.log('⏳ Waiting for confirmation...');
      await this.connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight
      });

      logger.log('✅ Burn transaction confirmed!');

      return {
        success: true,
        signature,
        amount: tokenAmount
      };

    } catch (error) {
      logger.error('❌ Error creating burn transaction:', error);
      throw error;
    }
  }

  // Verify burn transaction on-chain
  async verifyBurnTransaction(signature) {
    try {
      logger.log('🔍 Verifying burn transaction:', signature);

      // Wait a bit for the transaction to be available
      let attempts = 0;
      let tx = null;

      while (attempts < 10 && !tx) {
        try {
          tx = await this.connection.getTransaction(signature, {
            maxSupportedTransactionVersion: 0,
            commitment: 'confirmed'
          });

          if (!tx) {
            logger.log(`⏳ Transaction not found yet, attempt ${attempts + 1}/10...`);
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            attempts++;
          }
        } catch (err) {
          logger.log(`⏳ Waiting for transaction, attempt ${attempts + 1}/10...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }
      }

      if (!tx) {
        logger.warn('⚠️ Could not fetch transaction, but this might be temporary. Allowing burn to proceed.');
        return true; // Allow it through for now - transaction was signed
      }

      // Check if transaction was successful
      if (tx.meta?.err) {
        logger.error('❌ Transaction failed on-chain:', tx.meta.err);
        return false;
      }

      logger.log('✅ Burn transaction verified successfully');
      return true;
    } catch (error) {
      logger.error('Error verifying burn:', error);
      // If we can't verify, allow it through since the user signed it
      return true;
    }
  }

  // Process burn and grant boost
  async processBurn(userId, boostType, burnSignature, tokenAmount) {
    try {
      // Verify the burn transaction
      const isValid = await this.verifyBurnTransaction(burnSignature);
      if (!isValid) {
        throw new Error('Invalid burn transaction');
      }

      // Grant the boost based on type
      const boostData = this.getBoostData(boostType);
      
      // Update user document with boost
      const userRef = doc(db, 'users', userId);
      const userBoostsRef = doc(db, 'userBoosts', userId);
      
      // Create or update boosts document
      const boostsDoc = await getDoc(userBoostsRef);
      
      if (!boostsDoc.exists()) {
        await setDoc(userBoostsRef, {
          userId,
          activeBoosts: [],
          burnHistory: [],
          totalBurned: 0,
          createdAt: serverTimestamp()
        });
      }

      // Add new boost
      const newBoost = {
        id: `boost_${Date.now()}`,
        type: boostType,
        multiplier: boostData.multiplier,
        duration: boostData.duration,
        startTime: Date.now(),
        endTime: Date.now() + boostData.duration,
        burnSignature,
        tokenAmount,
        status: 'active'
      };

      await updateDoc(userBoostsRef, {
        activeBoosts: arrayUnion(newBoost),
        burnHistory: arrayUnion({
          signature: burnSignature,
          amount: tokenAmount,
          boostType,
          timestamp: Date.now()
        }),
        totalBurned: (boostsDoc.data()?.totalBurned || 0) + tokenAmount,
        lastBurn: serverTimestamp()
      });

      // Update user's boost status
      await updateDoc(userRef, {
        hasActiveBoost: true,
        currentBoostMultiplier: boostData.multiplier,
        boostEndTime: newBoost.endTime
      });

      // Handle instant effects (like prediction limit refresh)
      if (boostData.instant && boostType === 'refresh_prediction_limit') {
        logger.log('🔄 Resetting prediction limit for user:', userId);

        // Import ranking service to get user's tier
        const { default: xpService } = await import('./xpService');

        // Get user's current ranking to determine their tier limit
        const userRanking = await xpService.getUserRanking(userId);
        const rank = xpService.getRankByXP(userRanking.xp);

        // Get tier-based limits
        const tierLimits = {
          'paperhands': 5,
          'fartmark': 10,
          'gold': 25,
          'diamond': 100,
          'obsidian': 999 // unlimited (use high number)
        };

        const userLimit = tierLimits[rank.id] || 5;

        logger.log(`📊 User rank: ${rank.name}, Daily limit: ${userLimit}`);

        // Reset the daily prediction tracking by clearing today's prediction count
        // The system will recalculate from activeCalls filtered by today's date
        const userRankingRef = doc(db, 'userRankings', userId);

        // Clear activeCalls that are from today to effectively reset the count
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Filter out today's predictions to reset the count
        const updatedActiveCalls = (userRanking.activeCalls || []).filter(call => {
          const callDate = call.placedAt?.toDate ? call.placedAt.toDate() : new Date(call.placedAt);
          return callDate < today; // Keep only yesterday's and older predictions
        });

        await updateDoc(userRankingRef, {
          activeCalls: updatedActiveCalls,
          lastUpdated: serverTimestamp()
        });

        logger.log(`✅ Prediction limit refreshed via burn! Reset to ${userLimit} predictions for ${rank.name} rank.`);
      }

      return {
        success: true,
        boost: newBoost
      };
    } catch (error) {
      logger.error('Error processing burn:', error);
      throw error;
    }
  }

  // Get boost data based on type
  getBoostData(boostType) {
    const boosts = {
      xp_2x_1hr: {
        multiplier: 2,
        duration: 60 * 60 * 1000, // 1 hour in ms
        name: '2x XP Boost (1 hour)'
      },
      xp_3x_1hr: {
        multiplier: 3,
        duration: 60 * 60 * 1000, // 1 hour
        name: '3x XP Boost (1 hour)'
      },
      xp_5x_1hr: {
        multiplier: 5,
        duration: 60 * 60 * 1000, // 1 hour
        name: '5x XP Boost (1 hour)'
      },
      extra_predictions: {
        multiplier: 1,
        duration: 30 * 24 * 60 * 60 * 1000, // 30 days
        name: '+10 Daily Predictions'
      },
      refresh_prediction_limit: {
        multiplier: 1,
        duration: 0, // Instant effect
        name: 'Refresh Prediction Limit',
        instant: true // Flag for instant processing
      },
      custom_badge: {
        multiplier: 1,
        duration: null, // Permanent
        name: 'Custom Badge'
      },
      blue_check: {
        multiplier: 1,
        duration: null, // Permanent
        name: 'Verified Status'
      }
    };

    return boosts[boostType] || boosts.xp_2x_7days;
  }

  // Check and apply active boosts when calculating XP
  async getActiveBoostMultiplier(userId) {
    try {
      const userBoostsRef = doc(db, 'userBoosts', userId);
      const boostsDoc = await getDoc(userBoostsRef);
      
      if (!boostsDoc.exists()) return 1;
      
      const activeBoosts = boostsDoc.data().activeBoosts || [];
      const now = Date.now();
      
      // Find highest active XP multiplier
      let maxMultiplier = 1;
      
      for (const boost of activeBoosts) {
        if (boost.status === 'active' && boost.endTime > now) {
          if (boost.type.startsWith('xp_') && boost.multiplier > maxMultiplier) {
            maxMultiplier = boost.multiplier;
          }
        }
      }
      
      return maxMultiplier;
    } catch (error) {
      logger.error('Error getting boost multiplier:', error);
      return 1;
    }
  }

  // Clean up expired boosts
  async cleanupExpiredBoosts(userId) {
    try {
      const userBoostsRef = doc(db, 'userBoosts', userId);
      const boostsDoc = await getDoc(userBoostsRef);
      
      if (!boostsDoc.exists()) return;
      
      const activeBoosts = boostsDoc.data().activeBoosts || [];
      const now = Date.now();
      
      const updatedBoosts = activeBoosts.map(boost => {
        if (boost.endTime && boost.endTime < now && boost.status === 'active') {
          return { ...boost, status: 'expired' };
        }
        return boost;
      });
      
      await updateDoc(userBoostsRef, {
        activeBoosts: updatedBoosts
      });
      
      // Check if user still has active boosts
      const hasActive = updatedBoosts.some(b => b.status === 'active');
      
      if (!hasActive) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          hasActiveBoost: false,
          currentBoostMultiplier: 1,
          boostEndTime: null
        });
      }
    } catch (error) {
      logger.error('Error cleaning up boosts:', error);
    }
  }
}

export default new TokenBurnService();